/* ONE unified timeline (SITE-RULES 11b).
 *
 * Not a visits list beside a notes list beside a scans list. "What has actually
 * happened with this person" is one question, and answering it in four places is how
 * nobody answers it. It opens on the most recent handful; the rest is asked for. */
import { useState } from 'react';
import type { EventKind } from '@acrostic/model';
import { EVENT_KINDS } from '@acrostic/model';
import { useAsync, useSource } from '../../data/SourceProvider.js';
import { Panel, NoneRow } from '../../components/Panel.js';
import { fmt } from '../../lib/fmt.js';

const OPENS_ON = 10;

export function Timeline({ id }: { id: string }) {
  const source = useSource();
  const [kind, setKind] = useState<EventKind | 'all'>('all');
  const [all, setAll] = useState(false);
  const { data, error } = useAsync(
    () => source.getTimeline(id, { limit: all ? 200 : OPENS_ON, ...(kind === 'all' ? {} : { kinds: [kind] }) }),
    [source, id, kind, all],
  );

  const kinds = Object.keys(EVENT_KINDS) as EventKind[];

  return (
    <Panel
      eyebrow="One stream, not four"
      title="Activity timeline"
      right={
        <select
          className="t-label-small" value={kind}
          onChange={(e) => { setKind(e.target.value as EventKind | 'all'); setAll(false); }}
        >
          <option value="all">Everything</option>
          {kinds.map((k) => <option key={k} value={k}>{EVENT_KINDS[k].label}</option>)}
        </select>
      }
    >
      {error && <NoneRow>Could not read the timeline. {error.message}</NoneRow>}
      {!error && !data && <NoneRow>Reading…</NoneRow>}
      {data && data.length === 0 && (
        <NoneRow>{kind === 'all' ? 'Nothing on record yet.' : 'Nothing of that kind.'}</NoneRow>
      )}
      {data && data.length > 0 && (
        <>
          <ol className="timeline">
            {data.map((e, i) => {
              const meta = EVENT_KINDS[e.kind] ?? { label: e.kind, tone: 'plain' as const };
              return (
                <li key={`${e.at}-${i}`} className={'tl ' + meta.tone}>
                  <div className="when t-caption muted">{fmt.date(e.at)}</div>
                  <div className="what">
                    <div className="t-label-large">{e.title ?? meta.label}</div>
                    {e.detail && <div className="t-body-small muted">{e.detail}</div>}
                    <div className="t-caption muted">
                      {meta.label}
                      {e.who ? ` · ${e.who}` : ''}
                      {e.source ? ` · ${e.source}` : ''}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
          {!all && data.length >= OPENS_ON && (
            <button type="button" className="btn t-label-small" style={{ marginTop: 12 }}
              onClick={() => setAll(true)}>Show earlier activity</button>
          )}
        </>
      )}
    </Panel>
  );
}
